interface FormatConstants {
  FRAMES_PER_HOUR: number;
  FRAMES_PER_MINUTE: number;
  FRAMES_PER_SECOND: number;
  EFFECTIVE_FRAME_RATE: number;
  MS_PER_DAY: number;
}

const DEFAULT_MS_PER_DAY = 86400000;

const REGEX = /^([0-2][0-9]):([0-9]{2}):([0-9]{2})[:|;]([0-2][0-9])$/;

const CONSTANTS: Record<string, FormatConstants> = {
  "29.97": {
    FRAMES_PER_HOUR: 108000,
    FRAMES_PER_MINUTE: 1800,
    FRAMES_PER_SECOND: 30,
    EFFECTIVE_FRAME_RATE: 29.97,
    MS_PER_DAY: 86486486.48648648,
  },
  "25": {
    FRAMES_PER_HOUR: 90000,
    FRAMES_PER_MINUTE: 1500,
    FRAMES_PER_SECOND: 25,
    EFFECTIVE_FRAME_RATE: 25,
    MS_PER_DAY: DEFAULT_MS_PER_DAY,
  },
  "24": {
    FRAMES_PER_HOUR: 86400,
    FRAMES_PER_MINUTE: 1440,
    FRAMES_PER_SECOND: 24,
    EFFECTIVE_FRAME_RATE: 24,
    MS_PER_DAY: DEFAULT_MS_PER_DAY,
  },
  "23.976": {
    FRAMES_PER_HOUR: 86400,
    FRAMES_PER_MINUTE: 1440,
    FRAMES_PER_SECOND: 24,
    EFFECTIVE_FRAME_RATE: 23.976,
    MS_PER_DAY: DEFAULT_MS_PER_DAY,
  },
  "23.98": {
    FRAMES_PER_HOUR: 86400,
    FRAMES_PER_MINUTE: 1440,
    FRAMES_PER_SECOND: 24,
    EFFECTIVE_FRAME_RATE: 23.976,
    MS_PER_DAY: DEFAULT_MS_PER_DAY,
  },
};

type TimecodeInput = string | number | { frameCount: number };

export default class Timecode {
  private readonly format: FormatConstants;

  private milliseconds = 0;
  private hours = 0;
  private minutes = 0;
  private seconds = 0;
  private frames = 0;

  constructor(input: TimecodeInput, frameRate = 29.97) {
    this.format = CONSTANTS[`${frameRate}`] || CONSTANTS["29.97"];

    if (typeof input === "string") {
      this.parseString(input);
    } else if (typeof input === "number") {
      this.setMilliseconds(input);
    } else if (input && typeof input.frameCount === "number") {
      this.setMilliseconds(
        (input.frameCount * 1000) / this.format.EFFECTIVE_FRAME_RATE
      );
    } else {
      throw new Error("invalid constructor arguments");
    }
  }

  private centeringAdjustment(): number {
    return ((1 / this.format.EFFECTIVE_FRAME_RATE) * 1000) / 2;
  }

  private parseString(value: string): void {
    if (REGEX.test(value) === false) {
      throw new Error("invalid timecode format");
    }
    const parts = REGEX.exec(value) as RegExpExecArray;
    this.hours = parseInt(parts[1], 10);
    this.minutes = parseInt(parts[2], 10);
    this.seconds = parseInt(parts[3], 10);
    this.frames = parseInt(parts[4], 10);

    const { FRAMES_PER_HOUR, FRAMES_PER_MINUTE, FRAMES_PER_SECOND } =
      this.format;
    const totalFrames =
      FRAMES_PER_HOUR * this.hours +
      FRAMES_PER_MINUTE * this.minutes +
      FRAMES_PER_SECOND * this.seconds +
      this.frames;
    this.milliseconds =
      ((1000 / this.format.EFFECTIVE_FRAME_RATE) * totalFrames) %
      this.format.MS_PER_DAY;
    this.milliseconds += this.centeringAdjustment();
  }

  private setMilliseconds(value: number): void {
    this.milliseconds = value % this.format.MS_PER_DAY;
    if (this.milliseconds < 0) {
      this.milliseconds += this.format.MS_PER_DAY;
    }
    this.milliseconds += this.centeringAdjustment();
    this.milliseconds = parseFloat(this.milliseconds.toFixed(4));
  }

  private getMilliseconds(): number {
    return this.milliseconds - this.centeringAdjustment();
  }

  private computeFields(): void {
    const {
      EFFECTIVE_FRAME_RATE,
      FRAMES_PER_SECOND,
      FRAMES_PER_MINUTE,
      FRAMES_PER_HOUR,
    } = this.format;
    const frameCount = Math.floor(
      (this.milliseconds / 1000) * EFFECTIVE_FRAME_RATE + 0.0005
    );
    this.frames = Math.floor(frameCount % FRAMES_PER_SECOND);
    this.seconds = Math.floor((frameCount / FRAMES_PER_SECOND) % 60);
    this.minutes = Math.floor((frameCount / FRAMES_PER_MINUTE) % 60);
    this.hours = Math.floor((frameCount / FRAMES_PER_HOUR) % 24);
  }

  getFrameCount(): number {
    return Number.parseInt(
      String((this.milliseconds * this.format.EFFECTIVE_FRAME_RATE) / 1000),
      10
    );
  }

  add(other: Timecode): Timecode {
    this.setMilliseconds(this.getMilliseconds() + other.getMilliseconds());
    return this;
  }

  subtract(other: Timecode): Timecode {
    this.setMilliseconds(this.getMilliseconds() - other.getMilliseconds());
    return this;
  }

  toString(): string {
    this.computeFields();
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(this.hours)}:${pad(this.minutes)}:${pad(this.seconds)}:${pad(
      this.frames
    )}`;
  }
}
